import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import {
  BRAND, IMAGES, SERIES_INTRO, SERIES_INTRO_2, EPISODE_CHAPTERS, PEOPLE,
  JORDY_BIO_1, JORDY_BIO_2, JORDY_BIO_3, JORDY_STATS,
} from "../lib/content";
import { RevealLines, FadeUp } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { FlameInitial } from "../components/FlameInitial";
import { CornerWebs } from "../components/CornerWebs";
import { EditorialMarquee } from "../components/EditorialMarquee";
import { CastingJoinSection } from "../components/CastingJoinSection";

const SeriesEditorial = () => (
  <section data-testid="series-editorial" className="relative overflow-hidden pt-24 pb-16 md:pt-28 md:pb-20">
    <div className="pointer-events-none absolute -left-24 top-1/4 h-[460px] w-[460px] rounded-full bg-flame opacity-[0.13] blur-[150px]" />
    <div className="pointer-events-none absolute -right-24 bottom-0 h-[420px] w-[420px] rounded-full bg-crimson opacity-[0.10] blur-[150px]" />
    <CornerWebs />
    <div className="mx-auto grid max-w-7xl gap-10 px-6 md:grid-cols-2 md:items-start md:gap-16 md:px-10">
      <div>
        <p data-testid="series-eyebrow" className="font-gothic text-xs uppercase tracking-[0.35em] text-flame">The Series</p>
        <h1 className="mt-5 font-display text-5xl font-light leading-[1.02] tracking-tight text-white md:text-7xl">
          <RevealLines
            lines={[
              <><FlameInitial letter="O" size="0.9em" className="mr-[0.05em] translate-y-[0.06em]" />ne tattoo.</>,
              <>
                <span
                  style={{
                    backgroundImage: "linear-gradient(90deg, #FFD700 0%, #FF7A1A 50%, #E2341E 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  A world of stories.
                </span>
              </>,
            ]}
          />
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-white/60 md:text-lg">{SERIES_INTRO}</p>
        </FadeUp>
        <FadeUp delay={0.4}>
          <div className="mt-8">
            <h2 className="font-display text-2xl font-light leading-tight tracking-tight text-white md:text-3xl">
              A doorway into travel, identity &amp; transformation.
            </h2>
            <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-white/60">{SERIES_INTRO_2}</p>
          </div>
        </FadeUp>
      </div>
      <FadeUp delay={0.2}>
        <div className="relative">
          <div className="absolute -inset-3 -z-10 bg-flame/10 blur-2xl" />
          <img
            src={IMAGES.collaboration}
            alt="Tattoo artist and client during a session"
            className="h-[400px] w-full border border-white/10 object-cover md:h-[600px]"
          />
          <img src={BRAND.star} alt="" className="absolute -bottom-5 -right-5 w-14 opacity-80" />
        </div>
      </FadeUp>
    </div>
  </section>
);

const ChapterRow = ({ c, i }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-45% 0px -45% 0px" });
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  const active = isMobile && inView;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
      className="group grid grid-cols-1 items-baseline gap-4 py-8 transition-colors hover:bg-white/[0.02] md:grid-cols-[120px_1fr_2fr] md:gap-10 md:py-10"
    >
      <span className={`font-stat text-5xl font-bold transition-colors group-hover:text-flame md:text-6xl ${active ? "text-flame" : "text-white/15"}`}>{c.n}</span>
      <h3 className="font-display text-2xl font-normal text-white md:text-3xl">{c.title}</h3>
      <p className="font-body text-base leading-relaxed text-white/55">{c.body}</p>
    </motion.div>
  );
};

const InsideEpisode = () => (
  <section data-testid="inside-episode" className="relative overflow-hidden border-t border-white/10 pt-12 pb-16 md:pt-16 md:pb-20">
    <div className="mx-auto max-w-7xl px-6 md:px-10">
      <FadeUp>
        <p className="font-gothic text-xs uppercase tracking-[0.35em] text-flame">Inside an Episode</p>
        <h2 className="mt-5 max-w-3xl font-display text-4xl font-light tracking-tight text-white md:text-5xl">
          <FlameInitial letter="F" size="0.94em" className="mr-[0.04em] translate-y-[0.05em]" />ive movements.{" "}
          <span
            className="italic"
            style={{
              backgroundImage: "linear-gradient(90deg, #FFD700 0%, #FF7A1A 50%, #E2341E 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            One journey.
          </span>
        </h2>
      </FadeUp>

      <div className="mt-16 divide-y divide-white/10 border-y border-white/10">
        {EPISODE_CHAPTERS.map((c, i) => (
          <ChapterRow key={c.n} c={c} i={i} />
        ))}
      </div>

      <FadeUp>
        <p
          data-testid="inside-episode-closing"
          className="mx-auto mt-16 max-w-6xl text-center font-display text-xl font-light leading-tight tracking-tight md:mt-20 md:text-[2rem]"
          style={{
            backgroundImage: "linear-gradient(90deg, #FFD700 0%, #FF7A1A 50%, #E2341E 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          Artist and client begin as strangers from different countries.
          <br />
          The tattoo becomes a permanent symbol of the bond forged between them.
        </p>
      </FadeUp>
    </div>
  </section>
);

const People = () => (
  <section data-testid="the-people" className="relative overflow-hidden pt-10 pb-16 md:pt-12 md:pb-20">
    <div className="pointer-events-none absolute right-0 top-1/3 h-[400px] w-[400px] rounded-full bg-crimson opacity-10 blur-[130px]" />
    <div className="mx-auto max-w-7xl px-6 md:px-10">
      <FadeUp className="text-center">
        <p className="font-gothic text-xs uppercase tracking-[0.35em] text-flame">The People</p>
        <h2 className="mt-4 font-display text-4xl font-light tracking-tight text-white md:text-5xl">
          <FlameInitial letter="T" size="0.86em" className="mr-[0.04em] translate-y-[0.04em]" />hree perspectives.{" "}
          <span
            className="italic"
            style={{
              backgroundImage: "linear-gradient(90deg, #FFD700 0%, #FF7A1A 50%, #E2341E 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            One culture.
          </span>
        </h2>
      </FadeUp>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        {PEOPLE.map((p, i) => (
          <FadeUp key={p.n} delay={i * 0.1}>
            <div className="group relative h-full border border-white/10 bg-surface/40 p-6 transition-colors hover:border-flame/60">
              <img src={BRAND.web} alt="" className="absolute right-2 top-2 w-10 opacity-20 transition-opacity group-hover:opacity-60" />
              <span className="font-stat text-3xl font-bold text-flame">{p.n}</span>
              <p className="mt-3 font-gothic text-xs uppercase tracking-[0.25em] text-white/50">{p.label}</p>
              <h3 className="mt-3 min-h-[4.5rem] whitespace-pre-line font-display text-2xl font-normal leading-snug text-white">{p.title}</h3>
              <p className="mt-4 font-body text-sm leading-relaxed text-white/55">{p.body}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Jeffrey = () => (
  <section data-testid="exec-producer" className="relative overflow-hidden border-t border-white/10 py-24 md:py-32">
    <div className="pointer-events-none absolute left-[22%] top-1/2 h-[560px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-flame opacity-[0.14] blur-[160px]" />
    <div className="pointer-events-none absolute left-[8%] bottom-[6%] h-[380px] w-[420px] rounded-full bg-crimson opacity-[0.10] blur-[160px]" />
    <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-6 md:grid-cols-[1fr_0.8fr] md:px-10">
      <div className="flex flex-col justify-center">
        <p className="font-gothic text-xs uppercase tracking-[0.35em] text-flame">Executive Producer</p>
        <h2 className="mt-5 font-display text-5xl font-normal tracking-tight text-white md:text-6xl">
          <FlameInitial letter="J" size="0.8em" className="mr-[0.03em] translate-y-[0.04em]" />effrey{" "}
          <FlameInitial letter="F" size="0.8em" className="mr-[0.03em] translate-y-[0.04em]" /><span className="italic">uchs</span>
        </h2>
        <div className="mt-8 grid max-w-xl gap-5">
          <p className="font-body text-base leading-relaxed text-white/60">
            Philadelphia-born and Penn State educated, Jeffrey Fuchs is a lifelong entrepreneur whose experience spans real estate, business development, operations, emergency medicine, wrestling coaching, live events and performance.
          </p>
          <p className="font-body text-base leading-relaxed text-white/60">
            An EMT, certified ITTF table tennis coach, DJ and professional MC, Jeff also has a history in musical theater as both a performer and producer. He brings an unusually broad set of skills&mdash;and an even broader tolerance for Jordy&apos;s ideas&mdash;to Tattooed Abroad.
          </p>
          <p className="font-body text-base leading-relaxed text-white/60">
            As Executive Producer, he oversees the business and financial side of the series while serving as a trusted creative counterweight, helping ensure big ideas remain executable without losing what made them exciting in the first place.
          </p>
          <p className="font-body text-base leading-relaxed text-white/60">
            Jeff is right most of the time. Jordy disputes the exact figure and currently places it at 83.5%.
          </p>
        </div>
      </div>
      <FadeUp>
        <div className="relative">
          <div className="absolute -inset-3 -z-10 bg-crimson/15 blur-2xl" />
          <img src="/brand/jeffrey-fuchs.webp" alt="Jeffrey Fuchs" className="aspect-[3/4] w-full border border-white/10 object-cover" />
          <img src={BRAND.star} alt="" className="absolute -bottom-6 -left-6 w-16 opacity-80" />
        </div>
      </FadeUp>
    </div>
  </section>
);

const Creator = () => (
  <section data-testid="creator" className="relative overflow-hidden border-t border-white/10 py-24 md:py-32">
    <div className="pointer-events-none absolute right-[22%] top-1/2 h-[560px] w-[620px] translate-x-1/2 -translate-y-1/2 rounded-full bg-flame opacity-[0.14] blur-[160px]" />
    <div className="pointer-events-none absolute right-[8%] bottom-[6%] h-[380px] w-[420px] rounded-full bg-crimson opacity-[0.10] blur-[160px]" />
    <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-6 md:grid-cols-[0.8fr_1fr] md:px-10">
      <FadeUp>
        <div className="relative">
          <div className="absolute -inset-3 -z-10 bg-crimson/15 blur-2xl" />
          <img src={IMAGES.jordy} alt="Jordy Trachtenberg" className="aspect-[3/4] w-full border border-white/10 object-cover" />
          <img src={BRAND.star} alt="" className="absolute -bottom-6 -right-6 w-16 opacity-80" />
        </div>
      </FadeUp>
      <div className="flex flex-col justify-center">
        <p className="font-gothic text-xs uppercase tracking-[0.35em] text-flame">Creator · Host · Executive Producer</p>
        <h2 className="mt-5 font-display text-5xl font-normal tracking-tight text-white md:text-6xl">
          <FlameInitial letter="J" size="0.8em" className="mr-[0.03em] translate-y-[0.04em]" />ordy{" "}
          <span className="italic"><FlameInitial letter="T" size="0.8em" className="mr-[0.03em] translate-y-[0.04em]" />rachtenberg</span>
        </h2>
        <div className="mt-8 grid max-w-xl gap-5">
          <p className="font-body text-base leading-relaxed text-white/60">{JORDY_BIO_1}</p>
          <p className="font-body text-base leading-relaxed text-white/60">{JORDY_BIO_2}</p>
          <p className="font-body text-base leading-relaxed text-white/60">{JORDY_BIO_3}</p>
        </div>
        <div className="mt-10 flex flex-wrap gap-10 border-t border-white/10 pt-8">
          {JORDY_STATS.map((s) => (
            <div key={s.label}>
              <div className="font-stat text-4xl font-bold text-fire">{s.value}</div>
              <div className="mt-1 font-body text-xs uppercase tracking-[0.25em] text-white/50">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default function Series() {
  return (
    <>
      <Seo
        title="The Documentary Series | Tattooed Abroad"
        description="Explore the concept behind Tattooed Abroad: one tattoo, an artist, a client, and the personal stories that connect them across cultures."
      />
      <SeriesEditorial />
      <InsideEpisode />
      <EditorialMarquee gap="mt-0" />
      <People />
      <Jeffrey />
      <Creator />
      <CastingJoinSection source="series" />
    </>
  );
}
